import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { leaveFullscreen, makeBackgroundInert, wrapTabFocus } from "@/lib/modal-focus";
import { useSessionIdleTimeout } from "@/hooks/use-session-idle-timeout";

/**
 * Global session watchdog: idle sign-out (8 h by default, N16) shared across tabs, with a "Still there?"
 * prompt during the last minute (AUTH-1, AUTH-4).
 */
export function SessionIdleGuard({ children }: { children: React.ReactNode }) {
  const { warningVisible, secondsLeft, staySignedIn } = useSessionIdleTimeout();
  return (
    <>
      {children}
      {warningVisible && <StillTherePrompt secondsLeft={secondsLeft} onStay={staySignedIn} />}
    </>
  );
}

function StillTherePrompt({ secondsLeft, onStay }: { secondsLeft: number; onStay: () => void }) {
  const t = useTranslations("session");
  const buttonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // SPA-12 (ACC): a real modal — the page behind is inert, Tab stays inside, and focus goes back
  // to where the user was when it closes.
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    // W-30: a full-screen lesson player would hide this prompt (and be made inert with the page).
    leaveFullscreen();
    const restoreBackground = rootRef.current ? makeBackgroundInert(rootRef.current) : () => {};
    buttonRef.current?.focus();
    return () => {
      restoreBackground();
      if (opener && opener !== document.body && opener.isConnected) opener.focus();
    };
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onStay();
      return;
    }
    wrapTabFocus(e, rootRef.current);
  }

  return createPortal(
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-still-there-title"
        aria-describedby="session-still-there-body"
        data-testid="session-still-there"
        onKeyDown={handleKeyDown}
        className="w-full max-w-sm rounded-xl bg-[var(--surface-1)] p-6 shadow-lg"
      >
        <h2 id="session-still-there-title" className="mb-2 text-lg font-semibold">
          {t("stillThereTitle")}
        </h2>
        <p id="session-still-there-body" className="mb-4 text-sm text-[var(--text-muted)]">
          {t("stillThereBody", { seconds: secondsLeft })}
        </p>
        <button
          ref={buttonRef}
          type="button"
          onClick={onStay}
          data-testid="session-stay-signed-in"
          className="w-full rounded-md bg-[var(--accent-strong)] px-4 py-2 text-[var(--accent-foreground)] hover:bg-[var(--accent-strong-hover)]"
        >
          {t("staySignedIn")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
